import React from 'react'
import styled from 'styled-components'
import SEO from 'src/components/common/SEO'
import { LogoSyncContextProvider } from 'src/components/common/AnnotationContext'
import MatchTicker from 'src/components/common/MatchTicker'
import useIsMobile from 'src/modules/hooks/useIsMobile'
import useIsDesktop from 'src/modules/hooks/useIsDesktop'
import {
  ChannelSection,
  DealSection,
  FAQSection,
  HeroSection,
  Layout,
  NetworkSection,
  PlatformSection,
} from 'src/components/common'
import { PlanCardsWithTabs } from 'src/components/common/PlanCardsWithTabs'
import { CALowestSportsPrice } from 'src/constants/planData/canadaPlanData'
import HeroFootNote from 'src/components/common/HeroSectionNew/components/HeroFootNote'
import HeaderSignIn from 'src/components/common/HeaderSignIn'
import { WEB_MARKETING_HEADER_SIGN_IN } from 'src/modules/reducers/cohorts/constants'
import { useCohortFlag } from 'src/modules/hooks/useCohortFlag'

const CanadaTemplateDefault = ({
  lang = 'en',
  market = 'canada',
  // Offer Code
  offerCode,
  offerBanner,
  // Addon Code
  addonCode,
  // Metadata
  pageTitle,
  pageDescription,
  // Hero Section
  logo,
  logoHeight,
  title,
  subtitle,
  tagline,
  ctaText,
  video,
  videoMobile,
  staticAnnotation,
  annotations,
  poster,
  heroCtaNote,
  // Plan Cards
  plansTitle,
  plansSubtitle,

  // Network Section
  networkHeadline,
  networkSubtitle,
  networkSet,
  customNetworks,
  // FAQ Section
  additionalFAQs,
  // Others
  team,
  // Show offer input for unique codes
  hasOfferInput,
  // Place anchor links on all CTAs to plan cards
  forceAnchorLink = true,
  // AB Test - Video vs Logo Soup
  noVideo = false,
  // AB Test for MatchTicker
  matchtickerSportId,
  matchtickerLeagueName,
  matchtickerLeagueId,
  // Plan Card Override
  planCardOverride,
  FootnoteSection,
  // Personalization Params
  sportId,
  leagueId,
  teamId,

  usRedirectUrl,
  faqListOverride,
  hideChannelSection = false,
  hideNetworkSection = false,
  hideFAQSection = false,
  hidePlatformSection = false,
  hideDealSection = false,
  channelPlanCode,
  platformImage,
  platformHeadline,
  disableFreeTrial,

  // Plan Cards with Tabs
  hasPlanCardsTabs = true,
  planCardsConfig = {},
  valuePropsCopy,
  noIndex,
}) => {
  let metaDescription =
    pageDescription ||
    'See every match all season long. Stream live TV without cable. DVR included. No contract, cancel anytime.'
  const isMobile = useIsMobile()
  const isDesktop = useIsDesktop()
  const showHeaderSignIn = useCohortFlag(WEB_MARKETING_HEADER_SIGN_IN)

  const hasSoccer = planCardsConfig?.plans?.some((plan) => plan.plan === 'sports')
  const hasEntertainment = planCardsConfig?.plans?.some((plan) => plan.planType === 'entertainment')

  const displayDefaultSportsPromoMessaging =
    hasSoccer && !hasOfferInput && planCardsConfig?.displayMode !== 'plans'
  const isAnchor = forceAnchorLink && hasPlanCardsTabs ? '#select-plan' : null

  if (!offerBanner && displayDefaultSportsPromoMessaging) {
    offerBanner = {
      title: [
        'Special offer available.',
        CALowestSportsPrice
          ? `Enjoy live sports for as low as $${CALowestSportsPrice}/mo.`
          : 'Enjoy live sports at a low price.',
      ],
    }
  }

  const heroFootnote = valuePropsCopy ? (
    <HeroFootNote lang={lang} valuePropsCopy={valuePropsCopy} />
  ) : null

  return (
    <Layout
      lang={lang}
      market={market}
      offerCode={offerCode}
      addonCode={addonCode}
      offerBanner={offerBanner}
      ctaHrefOverride={isAnchor}
      sportId={sportId}
      leagueId={leagueId}
      teamId={teamId}
      usRedirectUrl={usRedirectUrl}
      noIndex={noIndex}
    >
      {showHeaderSignIn && (
        <HeaderSignIn
          lang={lang}
          market={market}
          disableFreeTrial={disableFreeTrial}
          ctaText={ctaText}
        />
      )}

      <LogoSyncContextProvider annotations={annotations}>
        <PageWrapper>
          <SEO
            lang={lang}
            market={market}
            title={pageTitle || title}
            description={metaDescription}
          />
          {matchtickerLeagueId && isMobile && (
            <MatchTicker
              lang={lang}
              market={market}
              sportId={matchtickerSportId}
              leagueName={matchtickerLeagueName}
              leagueId={matchtickerLeagueId}
            />
          )}
          <HeroSection
            logo={logo}
            logoHeight={logoHeight}
            lang={lang}
            market={market}
            heroBgVideo={video}
            heroBgVideoMobile={videoMobile}
            title={title}
            subtitle={subtitle}
            tagline={tagline}
            ctaText={ctaText}
            staticAnnotation={staticAnnotation}
            posterImg={poster}
            ctaHrefOverride={isAnchor}
            noVideo={noVideo}
            hasOfferInput={hasOfferInput}
            noteText={heroCtaNote}
            offerCode={offerCode}
            addonCode={addonCode}
            disableFreeTrial={disableFreeTrial}
            footnote={heroFootnote}
          />
          {matchtickerLeagueId && isDesktop && (
            <MatchTicker
              lang={lang}
              market={market}
              sportId={matchtickerSportId}
              leagueName={matchtickerLeagueName}
              leagueId={matchtickerLeagueId}
              disableFreeTrial={disableFreeTrial}
            />
          )}

          {hasPlanCardsTabs && (
            <PlanCardsWithTabs
              lang={lang}
              market={market}
              ctaText={ctaText}
              plans={planCardsConfig.plans}
              tabs={planCardsConfig.tabs}
              activeTab={planCardsConfig.activeTab}
              overrides={planCardsConfig.overrides}
              sharedData={planCardsConfig.sharedData}
              title={plansTitle || 'Which plan is right for you?'}
              hideTabs={planCardsConfig.hideTabs}
              displayMode={planCardsConfig.displayMode}
              tabSavings={planCardsConfig.tabSavings}
              // subtitle={
              //   plansSubtitle ?? "Canada's home for streaming live soccer and top entertainment"
              // }
            />
          )}

          {!hideNetworkSection && (
            <NetworkSection
              lang={lang}
              market={market}
              headline={networkHeadline}
              subtitle={networkSubtitle}
              networkSet={networkSet}
              customNetworks={customNetworks}
              ctaText={ctaText}
              ctaHrefOverride={isAnchor}
              hasOfferInput={hasOfferInput}
              disableFreeTrial={disableFreeTrial}
            />
          )}
          {!hideChannelSection && (
            <ChannelSection
              lang={lang}
              market={market}
              showAllChannels={true}
              title="Your Fubo channel lineup"
              ratePlanCode={channelPlanCode}
              disableFreeTrial={disableFreeTrial}
            />
          )}
          {!hideDealSection && <DealSection lang={lang} market={market} />}
          {!hidePlatformSection && (
            <PlatformSection
              lang={lang}
              market={market}
              ctaText={ctaText}
              hasOfferInput={hasOfferInput}
              ctaHrefOverride={isAnchor}
              platformImage={platformImage}
              platformHeadline={platformHeadline}
              disableFreeTrial={disableFreeTrial}
            />
          )}
          {!hideFAQSection && (
            <FAQSection
              lang={lang}
              market={market}
              team={team}
              canadaEnt={hasEntertainment}
              additionalFAQs={additionalFAQs}
              faqListOverride={faqListOverride}
              disableFreeTrial={disableFreeTrial}
            />
          )}
          {FootnoteSection && <FootnoteSection />}
        </PageWrapper>
      </LogoSyncContextProvider>
    </Layout>
  )
}

export default CanadaTemplateDefault

const PageWrapper = styled.div`
  .hero-content-text {
    h1 span {
      display: inline-block;
    }
  }
  .hero-placeholder {
    min-height: 700px;
    color: #fff;
    background: #333333;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`
